import { useAuthService } from '@/api/authService';
import { useAuthStore } from '@/stores/authStore';
import { useUserStore } from '@/stores/userStore';
import { AppSwitcher } from '@teamworksdev/vuetify';
import { computed } from 'vue';
import { isNil } from 'lodash-es';
import { handleError } from '@/utils/errorHandling';
import { encodeTwrn, normalizeTwrn } from '@/utils/twrn';

export function useAppSwitcher() {
	const authService = useAuthService();
	const authStore = useAuthStore();
	const userStore = useUserStore();

	async function getTokenFn() {
		try {
			const { token } = await authService.getOutgoingAxleToken();
			return token;
		} catch (e) {
			handleError(e);
			return '';
		}
	}

	const appSwitcher = computed(() => {
		if (!userStore.userAxleProfile?.loginAccess?.['web']) return null;
		return new AppSwitcher(
			AppSwitcher.slugs.INFLCR,
			getTokenFn,
			userStore.userAxleProfile.appTargetUrls,
			userStore.userAxleProfile.loginAccess['web'],
			!!authStore.impersonationToken,
		);
	});

	/**
	 * Deep links to an athlete's Hub profile via their TWRN in a new browser tab.
	 * An optional `section` (e.g. `attributes?openedCategories=`, `forms`) targets
	 * a sub-page of the profile.
	 * See HUB-11661 / the "Deep Linking to Hub Profiles via TWRN" platform doc.
	 */
	async function redirectToHubProfile(twrn: string, section?: string): Promise<void> {
		const _appSwitcher = appSwitcher.value;

		if (isNil(_appSwitcher)) {
			throw new Error('[useAppSwitcher] AppSwitcher not available');
		}

		const encodedTwrn = encodeTwrn(normalizeTwrn(twrn));
		const redirectPath = section
			? `/profiles/${encodedTwrn}/${section}`
			: `/profiles/${encodedTwrn}`;

		const error = await _appSwitcher.switch(
			AppSwitcher.slugs.HUB,
			{ redirect: redirectPath },
			{ openInNewTab: true },
		);
		if (error) throw error;
	}

	/**
	 * Deep links to an athlete's page in GM using their Axle TWRN in a new browser tab.
	 */
	async function redirectToGmProfile(twrn: string, target: 'agreements' | 'payments'): Promise<void> {
		const _appSwitcher = appSwitcher.value;

		if (isNil(_appSwitcher)) {
			throw new Error('[useAppSwitcher] AppSwitcher not available');
		}

		const error = await _appSwitcher.switch(
			AppSwitcher.slugs.GM,
			{ target, axleTwrn: normalizeTwrn(twrn) },
			{ openInNewTab: true },
		);
		if (error) throw error;
	}

	return {
		appSwitcher,
		redirectToHubProfile,
		redirectToGmProfile,
	};
}
